import React, { useState } from 'react'
import { ComposableMap, Geographies, Geography } from 'react-simple-maps'
import Tooltip from 'react-tooltip'
import {
  CountryNode,
  countryResolver,
  MapNode,
  mapResolver
} from './mapResolver'
import cn from 'classnames'

const geoUrl =
  'https://d2ad6b4ur7yvpq.cloudfront.net/naturalearth-3.3.0/ne_50m_admin_0_countries.geojson'

type WorldMapProps = {
  mapDataRaw: {
    text: string
  }
  introText: {
    html: string
  }
  description: {
    color: string
    text: {
      html: string
    }
  }[]
}

const WorldMap = ({ mapDataRaw, introText, description }: WorldMapProps) => {
  const [tooltipContent, setTooltipContent] = useState<JSX.Element | undefined>(
    undefined
  )

  const customMapData = mapResolver(mapDataRaw)

  const updateTooltip = (content?: JSX.Element) => setTooltipContent(content)

  return (
    <div className='page-slice-spacer'>
      <div
        className='article container'
        dangerouslySetInnerHTML={{ __html: introText.html }}
      />
      <Map updateTooltip={updateTooltip} customMapData={customMapData} />
      <Tooltip className='world-map__tooltip' backgroundColor='#0d0d0d'>
        {tooltipContent}
      </Tooltip>
      {description.map((item, i) => (
        <div key={i} className='world-map__description__item d-flex container'>
          <div className='offset-lg-2 col-lg-8'>
            <span
              className='world-map__description__item__color mt-1'
              style={{ background: item.color }}
            />
            <div
              className='article ml-2 parag--sm pl-2'
              dangerouslySetInnerHTML={{ __html: item.text.html }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

const MapTooltip = ({ country }: { country: CountryNode }) => (
  <div className='world-map__tooltip'>
    <h5>{country.IcelandicName}</h5>
    <ul>
      <li>
        <p className='parag--sm'>{country.type}</p>
      </li>
      {country.Utanrikisthjonusta && (
        <li>
          <p className='parag--sm'>{country.Utanrikisthjonusta}</p>
        </li>
      )}
      {country.OECDtype && (
        <li>
          <p className='parag--sm'>{country.OECDtype}</p>
        </li>
      )}
      {country.UNstatus && (
        <li>
          <p className='parag--sm'>{country.UNstatus}</p>
        </li>
      )}
      {country.MoreType && (
        <li>
          <p className='parag--sm'>{country.MoreType}</p>
        </li>
      )}
    </ul>
  </div>
)

const Map = ({
  updateTooltip,
  customMapData
}: {
  updateTooltip: (s?: JSX.Element) => void
  customMapData: MapNode[]
}) => {
  return (
    <div className='px-2 px-lg-3'>
      <ComposableMap data-tip='' className='world-map'>
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo, i) => {
              const country = countryResolver(customMapData, geo.properties)

              if (country) {
                return (
                  <Geography
                    key={i}
                    geography={geo}
                    onMouseEnter={() => {
                      updateTooltip(<MapTooltip country={country} />)
                    }}
                    onMouseLeave={() => updateTooltip()}
                    className={cn(
                      'world-map__geography',
                      `world-map__geography--${country.classNamePostfix}`
                    )}
                  />
                )
              }
            })
          }
        </Geographies>
      </ComposableMap>
    </div>
  )
}

export default WorldMap
